import { useId } from "react";
import { Badge, Select } from "../../design-system";
import type { AiModels, ModelPrice } from "./llm-data";
import "./llm-model-select.css";

/** Only supplied, finite prices can be compared. Missing prices never mean free. */
function priceFor(models: AiModels, model: string): ModelPrice | null {
  const price = models.prices?.[model];
  return price &&
    [price.input, price.cachedInput, price.output].every(
      (n) => Number.isFinite(n) && n >= 0,
    )
    ? price
    : null;
}
const usd = (value: number) =>
  "$" + value.toLocaleString("en-US", { maximumFractionDigits: 6 });

export function AiModelSelect({
  models,
  value,
  onChange,
  disabled = false,
  alwaysShow = false,
}: {
  models?: AiModels | null;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Retry forms can retain an existing model even when no current option is allowed. */
  alwaysShow?: boolean;
}) {
  const descriptionId = useId();
  if ((!models || !models.allowed.length) && !alwaysShow) return null;
  const source = models ?? { default: "", allowed: [] };
  const options = [...new Set(source.allowed)].sort((a, b) => {
    const left = priceFor(source, a),
      right = priceFor(source, b);
    if (!left || !right)
      return left ? -1 : right ? 1 : a.localeCompare(b, "en");
    return (
      left.output - right.output ||
      left.input - right.input ||
      a.localeCompare(b, "en")
    );
  });
  const cheapest = options.length ? priceFor(source, options[0]) : null;
  const cheapestModels = options.filter((model) => {
    const price = priceFor(source, model);
    return (
      price &&
      cheapest &&
      price.output === cheapest.output &&
      price.input === cheapest.input
    );
  });
  const isPlan = source.billing === "plan";
  const selected = value || source.default;
  const price = priceFor(source, selected);
  const lowestLabel = cheapestModels.length > 1 ? "공동 최저" : "최저";
  const describe = (model: string) => {
    if (isPlan) return `${model} · 플랜`;
    const item = priceFor(source, model);
    return `${model} · ${item ? `입력 ${usd(item.input)} / 출력 ${usd(item.output)}` : "단가 없음"}${cheapestModels.includes(model) ? ` · ${lowestLabel}` : ""}`;
  };
  return (
    <div data-ai-model-select className="lai-model-select">
      <Select
        label="AI 모델"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-describedby={descriptionId}
      >
        <option value="">
          기본 모델 · {source.default ? describe(source.default) : "미설정"}
        </option>
        {value && !options.includes(value) && (
          <option value={value}>
            현재 설정 · {describe(value)} (목록에 없음)
          </option>
        )}
        {options.map((model) => (
          <option key={model} value={model}>
            {describe(model)}
          </option>
        ))}
      </Select>
      <div className="lai-model-price" role="group" aria-label="선택 모델 단가">
        <div className="lai-model-price-heading">
          <strong>{selected || "기본 모델 미설정"}</strong>
          {!isPlan && cheapestModels.includes(selected) && (
            <Badge tone="teal">{lowestLabel}</Badge>
          )}
          <span>{isPlan ? "GPT 플랜" : "USD / 100만 토큰"}</span>
        </div>
        {isPlan ? (
          <p className="lai-model-price-empty">
            플랜 · 토큰만 기록 · 호출 비용 $0
          </p>
        ) : price ? (
          <dl>
            <div>
              <dt>입력</dt>
              <dd>{usd(price.input)}</dd>
            </div>
            <div>
              <dt>캐시 입력</dt>
              <dd>{usd(price.cachedInput)}</dd>
            </div>
            <div>
              <dt>출력</dt>
              <dd>{usd(price.output)}</dd>
            </div>
          </dl>
        ) : (
          <p className="lai-model-price-empty">
            단가 없음 · 예상 비용을 계산할 수 없습니다.
          </p>
        )}
      </div>
      <small id={descriptionId}>
        {isPlan
          ? "계정에서 제공한 모델 목록입니다. 사용량 한도는 플랜 계정에 적용됩니다."
          : "출력 → 입력 단가 순 · 동률은 모델명 순입니다. API 키 방식에만 적용하며, 로컬 세션은 PC의 모델 설정을 사용합니다."}
      </small>
    </div>
  );
}
